import { Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { PageNames } from 'src/app/configs/page-names.enum';
import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import { EventTypes } from 'src/app/core/constants/event-types';
import { AppFontSize } from 'src/app/core/types/app-font-size.type';
import {
  CustomResultOfPaginatedListOfOrganizationWithPaginationDto,
  CustomResultOfSecurityTokenDto,
  GetEnumValuesDto,
  GroupPriceByOrganizationIdDto,
  PaginatedListOfGetResellersWithPaginationByOrganizationIdDto,
} from '../../../core/api/models';
import { NumberDto } from '../../../core/api/models/number-dto';
import {
  AccountService,
  ExcelService,
  GroupPricesService,
  OrganizationTransactionsService,
} from '../../../core/api/services';
import { AuthenticatedMobileService } from '../../../core/api/services/authenticated-mobile.service';
import { OrganizationsService } from '../../../core/api/services/organizations.service';
import { AuthTokenService } from '../../../core/services/authentication/auth-token.service';
import { DialogStatus } from '../../../core/services/dialog.service';
import { AppDomService } from '../../../core/services/dom.service';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { OrganizationTransactionsFormComponent } from '../organization-transactions/organization-transactions-form/organization-transactions-form.component';
import { OrganizationTransactionsComponent } from '../organization-transactions/organization-transactions.component';
import { UserDocumentationFormModule } from '../users/user-documentation-form/user-documentation-form.module';
import { OrganizationWithPaginationDto } from './../../../core/api/models/organization-with-pagination-dto';
import { PaginatedListOfOrganizationWithPaginationDto } from './../../../core/api/models/paginated-list-of-organization-with-pagination-dto';
import { GroupPriceDetailsService } from './../../../core/api/services/group-price-details.service';
import { ResellersService } from './../../../core/api/services/resellers.service';
import { FormPageConfig } from './../../../core/models/form-page-config.model';
import { AuthService } from './../../../core/services/authentication/auth.service';
import { CreateOrganizationsExcelDialogComponent } from './create-organizations-excel/create-organizations-excel-dialog.component';
import { OrganizationDeleteConfirmDialogComponent } from './organization-delete-confirm-dialog/organization-delete-confirm-dialog.component';
import { OrganizationDocumentationComponent } from './organization-documentation/organization-documentation.component';
import { OrganizationGroupPriceDetailComponent } from './organization-group-price-detail/organization-group-price-detail.component';
import { OrganizationsAuthenticationFormComponent } from './organizations-authentication-form/organizations-authentication-form.component';
import { organizationsBlackListExceptionsComponent } from './organizations-black-list-exceptions/organizations-black-list-exceptions.component';
import { OrganizationsFormComponent } from './organizations-form/organizations-form.component';
import { organizationsResellersComponent } from './organizations-resellers/organizations-resellers.component';

@Component({
  selector: 'app-organizations',
  imports: [
    BaseIndexModule,
    organizationsResellersComponent,
    OrganizationTransactionsFormComponent,
    UserDocumentationFormModule,
    OrganizationDocumentationComponent,
    OrganizationsAuthenticationFormComponent,
    OrganizationsFormComponent,
    OrganizationTransactionsComponent,
    OrganizationGroupPriceDetailComponent,
    organizationsBlackListExceptionsComponent,
  ],
  templateUrl: './organizations.component.html',
})
export class OrganizationsComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties
  // isShowOrganizationResellers - isShowOrganizationGroupPriceDetail - isShowOrganizationTransaction - isShowUserDocumentation
  pageName = PageNames.organizations;

  modelWithPagination$: Observable<PaginatedListOfOrganizationWithPaginationDto>;
  items$: Observable<Array<OrganizationWithPaginationDto>>;
  formPageConfig: FormPageConfig<NumberDto> = {};

  fontSize: AppFontSize = 'sm';

  isShowAuthenticatedMobileAuditPage: boolean = false;
  isShowOrganizationAuthentication: boolean = false;
  isShowOrganizationDocumentation: boolean = false;
  isShowOrganizationTransaction: boolean = false;
  isShowOrganizationGroupPriceDetail: boolean = false;
  isShowOrganizationResellers: boolean = false;
  isShowUserDocumentation: boolean = false;
  isShowOrganizationBlackListExceptions: boolean = false;

  selectedOrganizationId: number;
  selectedOrganizationName: string;
  organizationGroupPriceDetail: GroupPriceByOrganizationIdDto;
  adminUserId: number;

  organizationTransactionFormPageConfig: FormPageConfig<any> = {};
  isLoginByIdentity: boolean;

  organizationResellersWithPagination$: Observable<PaginatedListOfGetResellersWithPaginationByOrganizationIdDto>;
  DocumentationTypeEnumValues: GetEnumValuesDto[] = [];
  UserDocumentsTypeEnumValues: GetEnumValuesDto[] = [];
  DocumentStatusEnumValues: GetEnumValuesDto[] = [];
  isShowOrganizationTransactions: boolean = false;

  /* Binding service endpoints */
  protected currentService = inject(OrganizationsService);
  createGetApiMethodName: string =
    this.currentService.organizationsCreateGet.name;
  deleteApiMethodName: string = this.currentService.organizationsDelete.name;
  auditApiMethodName: string =
    this.currentService.organizationsGetOrganizationsAudit.name;
  //#endregion

  constructor(
    protected groupPriceDetailService: GroupPriceDetailsService,
    protected groupPricesService: GroupPricesService,
    protected resellersService: ResellersService,
    protected authService: AuthService,
    public authenticatedMobileService: AuthenticatedMobileService,
    protected accountService: AccountService,
    protected OrganizationTransactionsService: OrganizationTransactionsService,
    protected excelFileService: ExcelService
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.getUserState();
    this.pageConfig.showAuditBtn = false;
    this.pageConfig.showAddBtnOnlyForSuperAdmin = false;
  }

  // وارد میشود چک میکنیم که کدام جدول باید نمایش داده شود url زمانی که کاربر با
  setPageAfterEnterUrl() {
    super.setPageAfterEnterUrl();
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      switch (key) {
        case QueryStringTypesEnum.Transaction_key:
          this.onShowChildPage({
            values: [...(value as string)?.split('_')],
            setUrl: false,
            queryStringKey: key,
            class: this,
            showPagePropKey:
              this.componentProperties.isShowOrganizationTransactions,
            selectedPropKey: [
              {
                keyName: this.componentProperties.selectedOrganizationId,
                keyType: typeof this.selectedOrganizationId,
              },
            ],
          });
          break;
        case QueryStringTypesEnum.Transaction_key:
          this.onShowChildPage({
            values: [...(value as string)?.split('_')],
            setUrl: false,
            queryStringKey: key,
            class: this,
            showPagePropKey:
              this.componentProperties.isShowOrganizationBlackListExceptions,
            selectedPropKey: [
              {
                keyName: this.componentProperties.selectedOrganizationId,
                keyType: typeof this.selectedOrganizationId,
              },
            ],
          });
          break;
        case QueryStringTypesEnum.Resellers_key:
          this.onShowChildPage({
            values: [...(value as string)?.split('_')],
            setUrl: false,
            queryStringKey: key,
            class: this,
            showPagePropKey:
              this.componentProperties.isShowOrganizationResellers,
            selectedPropKey: [
              {
                keyName: this.componentProperties.selectedOrganizationId,
                keyType: typeof this.selectedOrganizationId,
              },
            ],
          });
          break;
        case QueryStringTypesEnum.Documents_key:
          this.onShowDocumentations(
            value.toString().split('_')[0],
            !!value.toString().split('_')[1],
            +value.toString().split('_')[2],
            false
          );
          break;
        case QueryStringTypesEnum.Update_key:
          this.onUpdate(value, false);
          break;
        case QueryStringTypesEnum.GroupPriceDetail_key:
          this.onShowGroupPriceDetail(
            +value.toString().split('_')[0],
            value.toString().split('_')[1],
            false
          );
          break;
        case QueryStringTypesEnum.CreditTransfer_key:
          this.onCreditTransfer(value, false);
          break;
      }
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('OrganizationDocuments')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.DocumentationTypeEnumValues = values));
    this.getEnumValuesApi('UserDocuments')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.UserDocumentsTypeEnumValues = values));
    this.getEnumValuesApi('DocumentStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.DocumentStatusEnumValues = values));
  }

  ngAfterViewInit(): void {
    try {
      if (this.authService.isAuthenticated()) this.isLoginByIdentity = true;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  configTableColumns() {
    this.tableColumns = [
      {
        title: this.localizer.name,
        name: 'name',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.parentOrganization,
        name: 'parentName',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `common.domainName`,
        name: 'domainName',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.credit,
        name: 'credit',
        needCurrency: true,
        hasSearch: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}panelPrice`,
        name: 'panelPrice',
        needCurrency: true,
        hasSearch: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `common.defaultGroupPrice`,
        name: 'groupPriceCategoryTitle',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.active,
        name: 'isActive',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.activeSend,
        name: 'isActiveSend',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isAuthenticated`,
        name: 'isAuthenticated',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isPostpaid`,
        name: 'isPostpaid',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
      {
        title: this.localizer.expireDate,
        name: 'expireDate',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
    ];
  }

  //#region Api

  //Get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfOrganizationWithPaginationDto> {
    if (window.location.search.includes('key-'))
      this.clearFiltersAndSetDefaultSort();

    return this.currentService.organizationsGetOrganizationsWithPagination({
      body: this.getBaseModelWithPaginationParams(),
    });
  }

  //Get ById
  baseGetByIdApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .organizationsUpdateGet({ Id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserver);
  }

  // delete Organization With Descendant
  deleteOrganizationWithDescendantApi(id: number) {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .organizationsDeleteOrganizationWithDescendant({ id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  accountImpersonateUserApi(userGuid: string) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.accountService
      .accountImpersonateUser({ body: { userId: userGuid } })
      .pipe(this.basePipeOperators())
      .subscribe((result: CustomResultOfSecurityTokenDto) => {
        if (result.isSucceed) {
          AuthTokenService.onSetTokens(
            result.data.accessToken,
            result.data.refreshToken,
            false
          );
          window.location.replace(window.location.origin);
        } else
          this.dialogService.openDialog({
            title: 'common.error',
            message: result.message,
            status: DialogStatus.error,
          });
      });
  }

  createTransactionForOrganizationGetApi(id: number) {
    this.showParent = false;
    this.setParentQueryString();
    this.OrganizationTransactionsService.organizationTransactionsCreateTransactionForOrganizationGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          this.selectedOrganizationId = id;
          this.organizationTransactionFormPageConfig.vocabs =
            'pages.organizationTransactions.form.';
          this.isShowOrganizationTransaction = true;
        }
      });
  }

  onHideOrganizationTransaction() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowOrganizationTransaction = false;
  }

  getGroupPriceDetailByOrganizationIdApi(organizationId: number) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.groupPricesService
      .groupPricesGetGroupPriceByOrganizationId({
        body: { organizationId: organizationId },
      })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          this.isShowOrganizationGroupPriceDetail = true;
          this.organizationGroupPriceDetail = res.data;
        }
      });
  }

  organizationExcelReportApi() {
    this.excelFileService
      .excelCreateOrganizationExcelGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          let data = {
            title: `${this.pageVocabs}organizationsExcel`,
            comparisonObjects: this.comparisonObjects,
            sortObject: this.sortObject,
          };

          const modalResult = this.dialogService.openModal({
            component: CreateOrganizationsExcelDialogComponent,
            data: data,
          });

          modalResult
            .afterClosed()
            .pipe(
              takeUntil(this.onDestroy$),
              tap(() => {
                AppDomService.removeModalScrollClassFromHtml();
              })
            )
            .subscribe();
        }
      });
  }

  //#endregion

  //#region Authenticated Mobile Audit Page

  onisShowAuthenticatedMobileAuditPage() {
    this.isShowAuthenticatedMobileAuditPage = true;
  }

  onHideAuthenticatedMobileAuditPage() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowAuthenticatedMobileAuditPage = false;
  }
  //#endregion

  //#region organizationAuthentication and show documentations
  onAddAuthorization(id) {
    this.isShowOrganizationAuthentication = true;
    this.selectedOrganizationId = id;
  }

  onShowDocumentations(
    id,
    isRealPerson: boolean,
    adminUserIdInt: number,
    setUrl = true
  ) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Documents_key +
          '=' +
          id +
          '_' +
          isRealPerson +
          '_' +
          adminUserIdInt
      );
    }
    if (!isRealPerson) {
      this.isShowOrganizationDocumentation = true;
      this.selectedOrganizationId = id;
    } else {
      this.adminUserId = adminUserIdInt;
      this.isShowUserDocumentation = true;
    }
  }
  onCloseDocumentation() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowOrganizationDocumentation = false;
  }
  onCloseAuthorization() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowOrganizationAuthentication = false;
    this.pageConfig.loading = false;
  }

  //#endregion

  //#region CreditTransfer

  onCreditTransfer(id, setUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.CreditTransfer_key +
          '=' +
          id
      );
    }
    this.createTransactionForOrganizationGetApi(id);
  }

  //#endregion

  onDelete(model: OrganizationWithPaginationDto) {
    this.unSubs();

    let data = {
      itemName: model.name,
    };

    const modalResult = this.dialogService.openModal({
      component: OrganizationDeleteConfirmDialogComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((deleteType) => {
        if (deleteType) {
          if (deleteType == 'justOrganization') this.baseDeleteApi(model.id);
          else if (deleteType == 'organizationWihDescendantList')
            this.deleteOrganizationWithDescendantApi(model.id);
        }
      });
  }

  onLoginToOrganization(adminUserId) {
    this.accountImpersonateUserApi(adminUserId);
  }

  onExcelReport() {
    this.organizationExcelReportApi();
  }

  onShowResellers(organizationId: any, setUrl = true) {
    organizationId = +organizationId;
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Resellers_key +
          '=' +
          organizationId
      );
    }
    this.selectedOrganizationId = organizationId;
    this.isShowOrganizationResellers = true;
  }

  onShowOrganizationsBlackListExceptions(organizationId: any, setUrl = true) {
    organizationId = +organizationId;
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.OrganizationsBlackListExceptions_key +
          '=' +
          organizationId
      );
    }
    this.selectedOrganizationId = organizationId;
    this.isShowOrganizationBlackListExceptions = true;
  }

  onHideResellers() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowOrganizationResellers = false;
  }

  onHideOrganizationsBlackListExceptions() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowOrganizationBlackListExceptions = false;
  }

  onShowGroupPriceDetail(
    organizationId: number,
    organizationName: string,
    setUrl = true
  ) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.GroupPriceDetail_key +
          '=' +
          organizationId +
          '_' +
          organizationName
      );
    }
    this.selectedOrganizationName = organizationName;
    this.selectedOrganizationId = organizationId;
    this.getGroupPriceDetailByOrganizationIdApi(organizationId);
  }

  onShowTransactions(userId, setUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Transaction_key +
          '=' +
          userId
      );
    }
    this.selectedOrganizationId = userId;
    this.isShowOrganizationTransactions = true;
  }

  onHideTransactions() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowOrganizationTransactions = false;
  }
}
